'use client';

import type { MediaSlot } from '@selenos/leads';
import { ChevronDownIcon, MessageCircleIcon } from 'lucide-react';

import { MediaSlot as MediaSlotComponent } from '../../../_components/media';
import { smoothScrollTo, targetToId } from './smooth-scroll';

interface HeroSectionProps {
  cityName: string;
  averageHourlyEarnings: number;
  heroSlot: MediaSlot | null;
  whatsappUrl?: string;
  ctaTarget?: string;
  ctaLabel?: string;
  /** Override the main headline text. Falls back to "EARN R{rate}/HR". */
  headline?: string;
}

export function HeroSection({
  cityName,
  averageHourlyEarnings,
  heroSlot,
  whatsappUrl,
  ctaTarget = '#calculator',
  ctaLabel = 'Calculate Earnings',
  headline,
}: HeroSectionProps) {
  return (
    <section
      id="hero"
      className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-4"
      style={{ backgroundColor: 'var(--landing-secondary)' }}
    >
      {heroSlot && (
        <MediaSlotComponent
          slot={heroSlot}
          priority
          className="absolute inset-0 h-full w-full object-cover brightness-[0.25]"
        />
      )}

      {/* Gradient overlay */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(0,200,83,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Animated grain texture (CSS only) */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")",
          backgroundSize: '128px 128px',
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl text-center">
        {/* Headline */}
        <h1 className="text-5xl font-black tracking-tight text-white sm:text-7xl lg:text-8xl">
          {headline ?? `EARN R${String(averageHourlyEarnings)}/HR`}
        </h1>

        {/* City name */}
        <p
          className="mt-3 text-xl font-semibold sm:text-2xl"
          style={{ color: 'var(--landing-primary)' }}
        >
          in {cityName}
        </p>

        {/* Value prop */}
        <p className="mx-auto mt-4 max-w-md text-lg text-white/70">
          Drive when you want. Earn what you need.
        </p>

        {/* CTAs */}
        <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          {whatsappUrl && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-14 w-full max-w-sm items-center justify-center gap-2 rounded-xl text-lg font-bold text-black transition-opacity hover:opacity-90 sm:w-auto sm:px-10"
              style={{ backgroundColor: 'var(--landing-primary)' }}
              data-test="hero-whatsapp-cta"
            >
              <MessageCircleIcon className="h-5 w-5" />
              Apply via WhatsApp
            </a>
          )}
          <a
            href={ctaTarget}
            onClick={smoothScrollTo(targetToId(ctaTarget))}
            className="inline-flex h-14 w-full max-w-sm items-center justify-center rounded-xl border border-white/30 bg-white/5 text-lg font-semibold text-white transition-all hover:bg-white/10 sm:w-auto sm:px-10"
            data-test="hero-cta"
          >
            {ctaLabel}
          </a>
        </div>

        {/* Reassurance */}
        <p className="mt-4 text-sm text-white/50">
          See how much you could earn. No commitment.
        </p>
      </div>

      {/* Scroll arrow */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDownIcon className="h-6 w-6 text-white/30" />
      </div>
    </section>
  );
}
